import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse, AxiosRequestConfig } from 'axios';
import { parseApiError } from './api.errors';
import { ApiConfig } from './api.types';
import { authService } from '../auth.service';

const DEFAULT_CONFIG: ApiConfig = {
  baseURL: import.meta.env.VITE_API_BASE_URL || 'https://api.tendencia.ai/api/v1',
  timeout: Number(import.meta.env.VITE_API_TIMEOUT) || 30000,
  retryAttempts: Number(import.meta.env.VITE_API_RETRY_ATTEMPTS) || 3,
  enableLogging: import.meta.env.VITE_API_ENABLE_LOGGING === 'true',
  apiVersion: import.meta.env.VITE_API_VERSION || 'v1',
};

class ApiClient {
  private axiosInstance: AxiosInstance;
  private retryCount = new Map<string, number>();

  constructor(config: Partial<ApiConfig> = {}) {
    const finalConfig = { ...DEFAULT_CONFIG, ...config };

    this.axiosInstance = axios.create({
      baseURL: finalConfig.baseURL,
      timeout: finalConfig.timeout,
      withCredentials: true, // Enable credentials for CORS requests
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors(finalConfig);
  }

  private setupInterceptors(config: ApiConfig) {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (requestConfig: InternalAxiosRequestConfig) => {
        // Add auth token if available
        const token = this.getAuthToken();
        if (token) {
          requestConfig.headers.Authorization = `Bearer ${token}`;
        }

        // Log request if enabled
        if (config.enableLogging) {
          console.log('[API Request]', {
            method: requestConfig.method,
            url: requestConfig.url,
            params: this.sanitizeForLogging(requestConfig.params),
            data: this.sanitizeForLogging(requestConfig.data),
          });
        }

        return requestConfig;
      },
      (error: AxiosError) => {
        if (config.enableLogging) {
          console.error('[API Request Error]', error);
        }
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        // Log response if enabled
        if (config.enableLogging) {
          console.log('[API Response]', {
            status: response.status,
            data: this.sanitizeForLogging(response.data),
            url: response.config.url,
          });
        }

        // Clear retry count on success
        const requestId = this.getRequestId(response.config);
        this.retryCount.delete(requestId);

        return response;
      },
      async (error: AxiosError) => {
        if (config.enableLogging) {
          console.error('[API Response Error]', {
            status: error.response?.status,
            data: this.sanitizeForLogging(error.response?.data),
            url: error.config?.url,
          });
        }

        // Handle 401 Unauthorized - attempt token refresh
        if (error.response?.status === 401 && error.config) {
          // Skip retry if this is already a retry or auth endpoint
          const isAuthEndpoint = error.config.url?.includes('/auth/');
          const isRetry = (error.config as any)._retry;
          
          if (!isAuthEndpoint && !isRetry) {
            try {
              // Mark this request as a retry
              (error.config as any)._retry = true;
              
              // Try to refresh the token using authService
              const refreshToken = authService.getRefreshToken();
              
              if (refreshToken) {
                const result = await authService.refreshToken(refreshToken);
                // Token will be automatically set by authService
              }
              
              // Retry the original request with new token
              return this.axiosInstance.request(error.config);
            } catch (refreshError) {
              // Refresh failed, clear tokens and redirect to login
              authService.clearTokens();
              
              // Let the app handle the redirect
              throw parseApiError(error);
            }
          }
        }

        // Handle retry logic for other errors
        if (error.config && this.shouldRetry(error, config)) {
          const requestId = this.getRequestId(error.config);
          const currentRetryCount = this.retryCount.get(requestId) || 0;
          
          if (currentRetryCount < config.retryAttempts) {
            this.retryCount.set(requestId, currentRetryCount + 1);
            
            // Exponential backoff
            const delay = Math.min(1000 * Math.pow(2, currentRetryCount), 10000);
            await new Promise(resolve => setTimeout(resolve, delay));
            
            if (config.enableLogging) {
              console.log(`[API Retry] Attempt ${currentRetryCount + 1} of ${config.retryAttempts} after ${delay}ms`);
            }
            
            return this.axiosInstance.request(error.config);
          }
          
          this.retryCount.delete(requestId);
        }

        // Handle CORS-specific errors
        if (!error.response && error.code === 'ERR_NETWORK') {
          // Network error could be CORS issue
          if (config.enableLogging) {
            console.error('[CORS Debug] Network error detected - possible CORS issue:', {
              message: error.message,
              config: {
                url: error.config?.url,
                method: error.config?.method,
                withCredentials: error.config?.withCredentials,
                headers: this.sanitizeForLogging(error.config?.headers),
              }
            });
          }
        }

        // Parse and throw custom error
        throw parseApiError(error);
      }
    );
  }

  private getAuthToken(): string | null {
    // The authService uses TokenManager for secure token storage
    try {
      return authService.getAccessToken();
    } catch (error) {
      // Log error instead of silently falling back to insecure storage
      console.error('[API Client] Failed to get auth token from authService:', error);
      return null;
    }
  }

  private getRequestId(config: InternalAxiosRequestConfig): string {
    return `${config.method}-${config.url}-${JSON.stringify(config.params || {})}`;
  }

  private shouldRetry(error: AxiosError, config: ApiConfig): boolean {
    // Don't retry if retry is disabled
    if (config.retryAttempts === 0) return false;

    // Only retry on network errors or 5xx errors
    if (!error.response) return true; // Network error
    
    const status = error.response.status;
    return status >= 500 && status < 600;
  }

  private sanitizeForLogging(data: any): any {
    if (!data) return data;
    
    // Deep clone to avoid mutating original data
    const sanitized = JSON.parse(JSON.stringify(data));
    
    // List of sensitive fields to redact
    const sensitiveFields = [
      'password', 'token', 'secret', 'api_key', 'apiKey', 
      'authorization', 'credit_card', 'creditCard', 'ssn',
      'email', 'phone', 'address'
    ];
    
    const redactSensitiveData = (obj: any): any => {
      if (typeof obj !== 'object' || obj === null) return obj;
      
      Object.keys(obj).forEach(key => {
        const lowerKey = key.toLowerCase();
        if (sensitiveFields.some(field => lowerKey.includes(field))) {
          obj[key] = '[REDACTED]';
        } else if (typeof obj[key] === 'object') {
          obj[key] = redactSensitiveData(obj[key]);
        }
      });
      
      return obj;
    };
    
    return redactSensitiveData(sanitized);
  }

  // Public methods
  
  /**
   * Perform a GET request
   * @param url - The URL path (relative to base URL)
   * @param config - Optional axios request configuration
   * @returns Promise with the response data
   */
  get<T>(url: string, config?: AxiosRequestConfig) {
    return this.axiosInstance.get<T>(url, config);
  }

  /**
   * Perform a POST request
   * @param url - The URL path (relative to base URL)
   * @param data - The request body data
   * @param config - Optional axios request configuration
   * @returns Promise with the response data
   */
  post<T>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.axiosInstance.post<T>(url, data, config);
  }

  /**
   * Perform a PUT request
   * @param url - The URL path (relative to base URL)
   * @param data - The request body data
   * @param config - Optional axios request configuration
   * @returns Promise with the response data
   */
  put<T>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.axiosInstance.put<T>(url, data, config);
  }

  /**
   * Perform a PATCH request
   * @param url - The URL path (relative to base URL)
   * @param data - The request body data
   * @param config - Optional axios request configuration
   * @returns Promise with the response data
   */
  patch<T>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.axiosInstance.patch<T>(url, data, config);
  }

  /**
   * Perform a DELETE request
   * @param url - The URL path (relative to base URL)
   * @param config - Optional axios request configuration
   * @returns Promise with the response data
   */
  delete<T>(url: string, config?: AxiosRequestConfig) {
    return this.axiosInstance.delete<T>(url, config);
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export class for custom instances
export { ApiClient };